import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { transformSchoolRegistrationData, transformCommunityRegistrationData, transformIndependentRegistrationData, } from './utils/transformRegistrationData';
import Flow from '../Flow';
import { isDevToolsEnabled } from 'src/config';
import { trackNavigationButtonClicked, trackViewed } from 'src/tracking';
import { stepComponentsByUserType, } from './types';
import WhoIsRegistering from './steps/WhoIsRegistering';
/**
 * Parses URL query parameters for dev navigation
 * Only works in development/e2e environments
 * Usage: ?step=2&userType=school
 * @returns {{ step?: number; userType?: UserType } | null}
 */
const getDevNavigationParams = () => {
    if (!isDevToolsEnabled()) {
        return null;
    }
    const params = new URLSearchParams(window.location.search);
    const stepParam = params.get('step');
    const userTypeParam = params.get('userType');
    if (!stepParam && !userTypeParam) {
        return null;
    }
    const result = {};
    if (stepParam) {
        const step = parseInt(stepParam, 10);
        if (!Number.isNaN(step) && step >= 0) {
            result.step = step;
        }
    }
    if (userTypeParam && ['school', 'community', 'independent'].includes(userTypeParam)) {
        result.userType = userTypeParam;
    }
    return Object.keys(result).length > 0 ? result : null;
};
/**
 * Main registration flow component
 * Manages the multi-step registration process
 * @returns {JSX.Element} RegistrationFlow component
 */
const RegistrationFlow = () => {
    const [flowState, setFlowState] = useState(() => {
        // Initialize from query params in dev/e2e environments
        const devParams = getDevNavigationParams();
        if (devParams) {
            console.log('[Dev] Initializing from query params:', devParams);
            return {
                currentStep: devParams.step ?? 0,
                userType: devParams.userType,
                title: devParams.step === 0 ? "Who's registering?" : 'Loading...',
            };
        }
        return {
            currentStep: 0,
            userType: undefined,
            title: "Who's registering?",
        };
    });
    // Unified form data state
    const [formData, setFormData] = useState(() => {
        // Pre-populate userType selection if provided via query params
        const devParams = getDevNavigationParams();
        if (devParams?.userType) {
            return {
                whoIsRegistering: { userType: devParams.userType },
            };
        }
        return {};
    });
    const [pageValid, setPageValid] = useState(false);
    const [hideNavigation, setHideNavigation] = useState(false);
    const lastTrackedStepRef = useRef(undefined);
    const userTypeRef = useRef(flowState.userType);
    useEffect(() => {
        userTypeRef.current = flowState.userType;
    }, [flowState.userType]);
    useEffect(() => {
        if (lastTrackedStepRef.current === flowState.currentStep) {
            return;
        }
        lastTrackedStepRef.current = flowState.currentStep;
        trackViewed(flowState.currentStep, userTypeRef.current);
    }, [flowState.currentStep]);
    // When user navigates back from later steps, we need to find out whether the Next button is enabled.
    useEffect(() => {
        if (flowState.currentStep === 0) {
            setPageValid(Boolean(formData.whoIsRegistering?.userType));
        }
    }, [flowState.currentStep, formData.whoIsRegistering?.userType]);
    // Log dev navigation info on mount
    useEffect(() => {
        if (isDevToolsEnabled()) {
            const devParams = getDevNavigationParams();
            if (devParams) {
                console.log('[Dev] Flow initialized with step:', flowState.currentStep, 'userType:', flowState.userType);
            }
            else {
                console.log('[Dev] Navigation available via query params: ?step=N&userType=school|community|independent');
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    /**
     * Helper to update partial form data
     * @param {Partial<RegistrationFormData>} updates - Partial form data to update
     */
    const updateFormData = (updates) => {
        setFormData((prev) => ({ ...prev, ...updates }));
    };
    /**
     * Calculates the total number of steps based on user type
     * Step 0 is always "Who's registering?"
     * Then add the number of steps for the selected user type
     * @returns {number} Total number of steps
     */
    const getTotalSteps = () => {
        if (!flowState.userType) {
            return 1; // Just the "Who's registering?" step
        }
        // Step 0 (WhoIsRegistering) + user type specific steps
        const userTypeSteps = stepComponentsByUserType[flowState.userType].length;
        return userTypeSteps;
    };
    const totalSteps = getTotalSteps();
    /**
     * Handles who is registering data change
     * Updates both form data and flow state
     * @param {WhoIsRegisteringData} data - Who is registering data
     * @returns {void}
     */
    const handleWhoIsRegisteringChange = (data) => {
        updateFormData({ whoIsRegistering: data });
        setFlowState((prev) => ({
            ...prev,
            userType: data.userType,
        }));
    };
    /**
     * Handles navigation to next step
     * @returns {void}
     */
    const handleNext = () => {
        trackNavigationButtonClicked(flowState.currentStep, getNextButtonLabel(), flowState.userType);
        // Validate step 1 requires userType selection
        if (flowState.currentStep === 1 && !flowState.userType) {
            return;
        }
        // Disable next button.  Let the page content component handle the validation.
        setPageValid(false);
        setHideNavigation(false);
        setFlowState((prev) => ({
            ...prev,
            currentStep: prev.currentStep + 1,
        }));
    };
    /**
     * Handles navigation to previous step
     * @returns {void}
     */
    const handleBack = () => {
        setHideNavigation(false);
        trackNavigationButtonClicked(flowState.currentStep, 'Back', flowState.userType);
        setFlowState((prev) => ({
            ...prev,
            currentStep: Math.max(0, prev.currentStep - 1),
            // Reset title when going back to role selection
            title: prev.currentStep - 1 === 0 ? "Who's registering?" : prev.title,
        }));
    };
    /**
     * Updates the title in the flow state
     * @param {string} title - New title for the current step
     * @returns {void}
     */
    const setTitle = (title) => {
        setFlowState((prev) => ({
            ...prev,
            title,
        }));
    };
    /**
     * Renders the current step component based on flow state
     * @returns {JSX.Element | null} Current step component
     */
    const renderCurrentStep = () => {
        if (flowState.currentStep === 0) {
            return (_jsx(WhoIsRegistering, { initialData: formData.whoIsRegistering, onDataChange: handleWhoIsRegisteringChange, onPageValid: setPageValid, onHideNavigation: setHideNavigation }));
        }
        const stepIndex = flowState.currentStep - 1;
        // Type guard for school user type
        if (flowState.userType === 'school') {
            const schoolSteps = stepComponentsByUserType.school;
            switch (stepIndex) {
                case 0: {
                    const FindYourSchoolComponent = schoolSteps[0];
                    return (_jsx(FindYourSchoolComponent, { setTitle: setTitle, onPageValid: setPageValid, initialData: formData.findYourSchool, onDataChange: (data) => updateFormData({ findYourSchool: data }), onHideNavigation: setHideNavigation }));
                }
                case 1: {
                    const SchoolRegistrationComponent = schoolSteps[1];
                    // Merge school data from findYourSchool with any existing registration data
                    // The SchoolRegistration component will initialize defaults from its schema
                    const schoolData = formData.findYourSchool?.selectedSchool
                        ? {
                            schoolId: formData.findYourSchool.selectedSchool.id,
                            schoolName: formData.findYourSchool.selectedSchool.name,
                            zipCode: formData.findYourSchool.zipCode || '',
                        }
                        : {
                            schoolId: '',
                            schoolName: formData.findYourSchool?.schoolNotListed ? '' : '',
                            zipCode: formData.findYourSchool?.zipCode || '',
                        };
                    return (_jsx(SchoolRegistrationComponent, { setTitle: setTitle, onPageValid: setPageValid, initialData: { ...formData.schoolRegistration, ...schoolData }, onDataChange: (data) => updateFormData({ schoolRegistration: data }), onHideNavigation: setHideNavigation }));
                }
                case 2: {
                    const RegistrationSuccessComponent = schoolSteps[2];
                    // Cast FormValues to the expected shape - schema guarantees these fields
                    const registrationData = formData.schoolRegistration
                        ? transformSchoolRegistrationData(formData.schoolRegistration)
                        : undefined;
                    // Get school name from findYourSchool data
                    const schoolName = formData.findYourSchool?.selectedSchool?.name;
                    return (_jsx(RegistrationSuccessComponent, { setTitle: setTitle, onPageValid: setPageValid, initialData: formData.registrationSuccess ? { ...formData.registrationSuccess, schoolName } : { schoolName }, onHideNavigation: setHideNavigation, userType: flowState.userType, registrationData: registrationData }));
                }
                default:
                    return null;
            }
        }
        // Type guard for community user type
        if (flowState.userType === 'community') {
            const communitySteps = stepComponentsByUserType.community;
            switch (stepIndex) {
                case 0: {
                    const CommunityRegistrationComponent = communitySteps[0];
                    return (_jsx(CommunityRegistrationComponent, { setTitle: setTitle, onPageValid: setPageValid, initialData: formData.communityRegistration, onDataChange: (data) => updateFormData({ communityRegistration: data }), onHideNavigation: setHideNavigation }));
                }
                case 1: {
                    const RegistrationSuccessComponent = communitySteps[1];
                    const registrationData = formData.communityRegistration
                        ? transformCommunityRegistrationData(formData.communityRegistration)
                        : undefined;
                    // Get organization name from community registration data
                    const schoolName = (formData.communityRegistration?.organizationName ||
                        formData.communityRegistration?.orgName ||
                        formData.communityRegistration?.schoolName);
                    return (_jsx(RegistrationSuccessComponent, { setTitle: setTitle, onPageValid: setPageValid, initialData: formData.registrationSuccess ? { ...formData.registrationSuccess, schoolName } : { schoolName }, onHideNavigation: setHideNavigation, userType: flowState.userType, registrationData: registrationData }));
                }
                default:
                    return null;
            }
        }
        // Type guard for independent user type
        if (flowState.userType === 'independent') {
            const independentSteps = stepComponentsByUserType.independent;
            switch (stepIndex) {
                case 0: {
                    const IndependentRegistrationComponent = independentSteps[0];
                    return (_jsx(IndependentRegistrationComponent, { setTitle: setTitle, onPageValid: setPageValid, initialData: formData.independentRegistration, onDataChange: (data) => updateFormData({ independentRegistration: data }), onHideNavigation: setHideNavigation }));
                }
                case 1: {
                    const RegistrationSuccessComponent = independentSteps[1];
                    const registrationData = formData.independentRegistration
                        ? transformIndependentRegistrationData(formData.independentRegistration)
                        : undefined;
                    return (_jsx(RegistrationSuccessComponent, { setTitle: setTitle, onPageValid: setPageValid, initialData: formData.registrationSuccess, onHideNavigation: setHideNavigation, userType: flowState.userType, registrationData: registrationData }));
                }
                default:
                    return null;
            }
        }
        return null;
    };
    /**
     * Determines if the next/submit button should be disabled
     * @returns {boolean} True if button should be disabled
     */
    const isNextDisabled = () => {
        if (!flowState.userType) {
            return !pageValid;
        }
        return !pageValid;
    };
    /**
     * Determines the label for the next button
     * @returns {string} Button label ('Next' or 'Submit')
     */
    const getNextButtonLabel = () => {
        // Submit on the registration form step (before success page)
        if ((flowState.currentStep === 1 && flowState.userType === 'community') ||
            (flowState.currentStep === 1 && flowState.userType === 'independent') ||
            (flowState.currentStep === 2 && flowState.userType === 'school')) {
            return 'Submit';
        }
        return 'Next';
    };
    /**
     * Determines if we should hide step indicators
     * @returns {boolean} True if step indicators should be hidden
     */
    const shouldHideStepIndicators = () => {
        // Hide on success page (hideNavigation is true)
        return hideNavigation;
    };
    return (_jsx(Flow, { currentStep: flowState.currentStep, totalSteps: totalSteps, stepTitle: flowState.title, onNext: handleNext, onBack: handleBack, isNextDisabled: isNextDisabled(), nextButtonLabel: getNextButtonLabel(), hideNavigation: hideNavigation, hideStepIndicators: shouldHideStepIndicators(), children: renderCurrentStep() }));
};
export default RegistrationFlow;
